import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

// Boogie's type styles and its `text-edu-*` colors share the `text-` prefix,
// which stock tailwind-merge can't tell apart. The type scale is registered
// as font sizes and `text-edu-*` as text colors so they merge independently.
//
// Bare `@theme` size tokens live in their own `boogie-text-size` group: they
// set no line-height, so they must not drop a preceding `leading-*`. The
// composite `@utility` styles do set one and stay in `font-size`.
//
// Keep in sync with styles/tokens/typography.css (`--text-*` tokens and
// composite `@utility text-*` styles).
const BOOGIE_TEXT_SIZES = [
  'body-1',
  'body-2',
  'body-3',
  'headline-2',
  'headline-3',
  'label-2',
  'label-3',
  'title-1',
  'title-2',
  'title-3',
  'title-4',
]

const BOOGIE_TEXT_STYLES = [
  'body-1-emphasis',
  'body-2-emphasis',
  'body-3-emphasis',
  'body-4',
  'body-4-emphasis',
  'body-5',
  'label-1',
  'mono-1',
  'mono-1-emphasis',
  'mono-2',
  'mono-2-emphasis',
  'title-display-1',
  'title-display-2',
  'title-display-2-small',
  'title-display-3',
  'title-display-4',
  'title-serif-1',
  'title-serif-2',
  'title-serif-3',
  'title-serif-4',
]

// Keep in sync with styles/tokens/typography.css (`--leading-*`).
const BOOGIE_LINE_HEIGHTS = [
  'headline-2',
  'headline-3',
  'title-1',
  'title-2',
  'title-3',
  'title-4',
  'body-1',
  'body-2',
  'body-3',
  'label-1',
  'label-2',
  'label-3',
]

// Same shape of bug for the radius scale: stock tailwind-merge doesn't
// recognise `edu-*` as border-radius values, so `rounded-edu-md` and
// `rounded-edu-none` both survive a merge and stylesheet order decides the
// winner. Registering the scale is what makes the documented per-caller
// radius override on the `eduSurface*` recipes actually take effect.
//
// Keep in sync with styles/tokens/components.css (`--radius-edu-*`).
const BOOGIE_RADII = [
  'edu-none',
  'edu-sm',
  'edu-md',
  'edu-lg',
  'edu-xl',
  'edu-full',
]

// And for elevation: `shadow-edu-*` isn't a stock shadow size, so a caller's
// `shadow-none` would survive alongside the recipe's `shadow-edu-input` and
// stylesheet order would pick the winner.
//
// Keep in sync with styles/tokens/components.css (`--shadow-edu-*`).
const BOOGIE_SHADOWS = [
  'edu-input',
  'edu-card',
  'edu-card-lifted',
  'edu-inset',
  'edu-floating',
]

const twMerge = extendTailwindMerge<'boogie-text-size'>({
  extend: {
    classGroups: {
      'boogie-text-size': [{ text: BOOGIE_TEXT_SIZES }],
      'font-size': [{ text: BOOGIE_TEXT_STYLES }],
      'text-color': [{ text: [(value: string) => value.startsWith('edu-')] }],
      rounded: [{ rounded: BOOGIE_RADII }],
      'rounded-t': [{ 'rounded-t': BOOGIE_RADII }],
      'rounded-r': [{ 'rounded-r': BOOGIE_RADII }],
      'rounded-b': [{ 'rounded-b': BOOGIE_RADII }],
      'rounded-l': [{ 'rounded-l': BOOGIE_RADII }],
      leading: [{ leading: BOOGIE_LINE_HEIGHTS }],
      shadow: [{ shadow: BOOGIE_SHADOWS }],
    },
    conflictingClassGroups: {
      'boogie-text-size': ['font-size'],
      'font-size': ['boogie-text-size'],
    },
    conflictingClassGroupModifiers: {
      'boogie-text-size': ['leading'],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
